import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'src/components/ui/chart';
import { chartConfig } from 'src/features/forecast/configs/chartConfig';
import { linesConfig } from 'src/features/forecast/configs/linesConfig';
import { yAxesConfig } from 'src/features/forecast/configs/yAxesConfig';
import { useForecastChart } from 'src/features/forecast/hooks/useForecastChart';
import { cn } from 'src/lib/utils';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

type ForecastChartProps = {
  data: ForecastDataPoint[];
  className?: string;
  isNovelty?: boolean;
};

export function ForecastChart({
  data,
  className,
  isNovelty = false,
}: ForecastChartProps) {
  const { t } = useTranslation('forecast');
  const { chartData, currentMonthId, chartKey } = useForecastChart(
    data,
    isNovelty,
  );

  return (
    <ChartContainer config={chartConfig} className={cn(className)}>
      <ComposedChart
        key={chartKey}
        data={chartData}
        margin={{
          top: 20,
          right: 80,
          bottom: 60,
          left: 80,
        }}
      >
        <defs>
          <linearGradient id='stockGradient' x1='0' y1='0' x2='0' y2='1'>
            <stop
              offset='5%'
              stopColor='var(--chart-stock)'
              stopOpacity={0.4}
            />
            <stop
              offset='95%'
              stopColor='var(--chart-stock)'
              stopOpacity={0.1}
            />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray='3 3' stroke='var(--chart-grid)' />
        <XAxis
          dataKey='month_id'
          type='category'
          scale='point'
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: 'var(--chart-text)' }}
          angle={-45}
          textAnchor='end'
          height={100}
          interval={0}
          minTickGap={10}
        />
        {yAxesConfig.map((yAxisConfig) => (
          <YAxis
            key={yAxisConfig.yAxisId}
            {...yAxisConfig}
            label={{
              ...yAxisConfig.label,
              value: t(yAxisConfig.label.value),
            }}
          />
        ))}
        <ReferenceLine
          x={currentMonthId}
          yAxisId='sales'
          stroke='var(--chart-reference-line)'
          strokeWidth={2}
          strokeDasharray='5 5'
          label={{
            value: t('Forecast start'),
            position: 'top',
            fill: 'var(--chart-text)',
          }}
          ifOverflow='extendDomain'
        />
        <ChartTooltip
          content={<ChartTooltipContent className='bg-layout p-2' />}
          labelFormatter={(label) => `${t('Month')}: ${label}`}
        />
        {linesConfig.map((config) =>
          config.chartType === 'area' ? (
            <Area key={config.dataKey} type={'monotone'} {...config} />
          ) : (
            <Line key={config.dataKey} type={'monotone'} {...config} />
          ),
        )}
      </ComposedChart>
    </ChartContainer>
  );
}
